// Operators — invite-only management of KaziPeople platform staff. Only a
// platform_owner reaches this page (gated by platform-staff:manage). Invite a
// colleague by email + platform role; they set their own password via the emailed
// link. Internal domains only (enforced server-side).
import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, Select, Tag, message, Popconfirm, Space, Typography,
} from 'antd';
import { UserAddOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  listOperators, inviteOperator, revokeInvite, setOperatorActive,
  OPERATOR_ROLES, type Operator, type PendingInvite,
} from '../../services/operatorsService';

const roleColor: Record<string, string> = {
  platform_owner: 'gold',
  platform_admin: 'geekblue',
  platform_support: 'cyan',
  platform_billing: 'purple',
  platform_analyst: 'green',
};
const roleTag = (r: string) => (
  <Tag key={r} color={roleColor[r] || 'default'}>{r.replace('platform_', '')}</Tag>
);

const OperatorsPage: React.FC = () => {
  const [operators, setOperators] = useState<Operator[]>([]);
  const [pending, setPending] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const d = await listOperators();
      setOperators(d.operators || []);
      setPending(d.pendingInvites || []);
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not load operators.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submitInvite = async () => {
    const v = await form.validateFields();
    setSubmitting(true);
    try {
      await inviteOperator({ email: v.email.trim().toLowerCase(), name: v.name, role: v.role });
      message.success(`Invite sent to ${v.email}`);
      setInviteOpen(false); form.resetFields(); load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not send the invite.');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleActive = async (o: Operator) => {
    try {
      await setOperatorActive(o.id, !o.isActive);
      message.success(`${o.name} ${o.isActive ? 'deactivated' : 'activated'}`);
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not update the operator.');
    }
  };

  const revoke = async (inv: PendingInvite) => {
    try {
      await revokeInvite(inv.id);
      message.success(`Invite to ${inv.email} revoked`);
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not revoke the invite.');
    }
  };

  const opColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name', render: (n: string, o: Operator) => (
      <span>{n}{o.self && <Tag style={{ marginLeft: 8 }} color="blue">you</Tag>}</span>
    ) },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Roles', dataIndex: 'roles', key: 'roles', render: (rs: string[]) => (rs || []).map(roleTag) },
    { title: 'Status', dataIndex: 'isActive', key: 'isActive',
      render: (a: boolean) => <Tag color={a ? 'green' : 'red'}>{a ? 'Active' : 'Inactive'}</Tag> },
    { title: '', key: 'action', align: 'right' as const, render: (_: any, o: Operator) => {
      const isOwner = (o.roles || []).includes('platform_owner');
      if (o.self || isOwner) return <Typography.Text type="secondary">—</Typography.Text>;
      return (
        <Popconfirm
          title={`${o.isActive ? 'Deactivate' : 'Activate'} ${o.name}?`}
          onConfirm={() => toggleActive(o)} okText="Yes"
        >
          <Button size="small" danger={o.isActive}>{o.isActive ? 'Deactivate' : 'Activate'}</Button>
        </Popconfirm>
      );
    } },
  ];

  const inviteColumns = [
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Role', dataIndex: 'role', key: 'role', render: roleTag },
    { title: 'Expires', dataIndex: 'expiresAt', key: 'expiresAt',
      render: (d: string) => (d ? fmtDate(d) : '—') },
    { title: '', key: 'action', align: 'right' as const, render: (_: any, inv: PendingInvite) => (
      <Popconfirm title={`Revoke invite to ${inv.email}?`} onConfirm={() => revoke(inv)} okText="Revoke">
        <Button size="small" danger>Revoke</Button>
      </Popconfirm>
    ) },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <Typography.Title level={3} style={{ margin: 0 }}>Operators</Typography.Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
          <Button type="primary" icon={<UserAddOutlined />} onClick={() => setInviteOpen(true)}>Invite operator</Button>
        </Space>
      </div>
      <Typography.Paragraph type="secondary">
        KaziPeople platform staff. Invite colleagues (internal email only) — they set their own password
        from the emailed link. Only an owner can manage operators.
      </Typography.Paragraph>

      <Typography.Title level={5} style={{ marginTop: 16 }}>Active operators</Typography.Title>
      <Table rowKey="id" loading={loading} columns={opColumns} dataSource={operators} pagination={false} size="middle" />

      {pending.length > 0 && (
        <>
          <Typography.Title level={5} style={{ marginTop: 24 }}>Pending invites</Typography.Title>
          <Table rowKey="id" columns={inviteColumns} dataSource={pending} pagination={false} size="middle" />
        </>
      )}

      <Modal
        title="Invite a platform operator"
        open={inviteOpen}
        onOk={submitInvite}
        confirmLoading={submitting}
        onCancel={() => { setInviteOpen(false); form.resetFields(); }}
        okText="Send invite"
      >
        <Form form={form} layout="vertical" requiredMark={false} style={{ marginTop: 12 }}>
          <Form.Item name="email" label="Work email" rules={[
            { required: true, type: 'email', message: 'Enter a valid email' },
            { pattern: /@(kazipeople\.io|kazipeople\.com)$/i, message: 'Must be a @kazipeople.io or @kazipeople.com address' },
          ]}>
            <Input placeholder="name@kazipeople.com" autoComplete="off" />
          </Form.Item>
          <Form.Item name="name" label="Name (optional)">
            <Input placeholder="Jane Doe" autoComplete="off" />
          </Form.Item>
          <Form.Item name="role" label="Role" rules={[{ required: true, message: 'Pick a role' }]}>
            <Select placeholder="Select a platform role" options={OPERATOR_ROLES} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OperatorsPage;
